import { gql } from '@apollo/client';

export const GET_COOPERATION_WAREHOUSES_LISTING = gql`
  query GetCooperationWarehouseListing($filter: CooperationWarehouseFiltersTypeInput, $pageRequest: CooperationWarehousePageRequestTypeInput) {
    getCooperationWarehouseListing(filters: $filter, pageRequest: $pageRequest) {
      content{
        id
        name
        addressLine1
        addressLine2
        country
        state
        city
        zipcode
        warehouseName
        contactPersonList {
          name
          countryCode
          phone
          extension
        }
        aliasList
        relatedAddressList {
          address1
          address2
        }
        isNew
        continuousFailure
        standingPickup
        status
        linkStatus
        brand
        createdAt
        updatedAt
        remark
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

export const GET_COOPERATION_WAREHOUSE_BY_ID = gql`
  query getCooperationWarehouseById($id: Long!) {
    getCooperationWarehouseById(id: $id) {
      id
      name
      type
      warehouseId
      status
      addressLine1
      addressLine2
      country
      state
      city
      zipcode
      addressHash
      latitude
      longitude
      contactPersonList {
        name
        countryCode
        phone
        extension
        inBlacklist
      }
      email
      warehouseName
      workWeekdayList
      workStartTime
      workEndTime
      expectedPickupStartTime
      expectedPickupEndTime
      aliasList
      relatedAddressList {
        address1
        address2
      }
      isNew
      continuousFailure
      standingPickup
      createdAt
      updatedAt
      channelId
      relatedChannelIds
      useVehicleModelId
      # 车型口径为 vehicle_type，与 useVehicleModelId 不同 id 空间（实测同站点 model=4 / type=6）。
      # 详情弹窗与保存 payload 均只认 vehicleTypeId，缺此字段会让「车辆类型」恒显示 '-'；
      # useVehicleModelId 保留不删，与 GET_COOPERATION_WAREHOUSE_DETAIL 字段集对齐，避免同类型缓存字段不一致。
      vehicleTypeId
      remark
    }
  }
`;

/**
 * 合作仓详情（含揽收站点 2.0 专有字段），用于审核池「查看站点」只读详情。
 * 与 GET_COOPERATION_WAREHOUSE_BY_ID 查同一 resolver，仅多取 2.0 字段——独立成一条避免
 * 让站点管理/揽收任务等既有查询被迫依赖 2.0 schema（api-server 未升级时会 GraphQL 校验失败）。
 */
export const GET_COOPERATION_WAREHOUSE_DETAIL = gql`
  query getCooperationWarehouseByIdV2($id: Long!) {
    getCooperationWarehouseById(id: $id) {
      id
      name
      type
      warehouseId
      status
      addressLine1
      addressLine2
      country
      state
      city
      zipcode
      addressHash
      latitude
      longitude
      contactPersonList {
        name
        countryCode
        phone
        extension
        inBlacklist
      }
      email
      warehouseName
      workWeekdayList
      workStartTime
      workEndTime
      expectedPickupStartTime
      expectedPickupEndTime
      aliasList
      relatedAddressList {
        address1
        address2
      }
      isNew
      continuousFailure
      standingPickup
      createdAt
      updatedAt
      channelId
      relatedChannelIds
      useVehicleModelId
      otherRemark
      linkStatus
      warehouseCategory
      brand
      subBrand
      dock
      groupId
      groupName
      groupQrcodeS3Key
      standingPickupChannelId
      standingScheduleJson
      standardizeStatus
      addressCoreHash
      addressAnalysisHash
      vehicleTypeId
      remark
    }
  }
`;

export interface CooperationWarehouseFiltersTypeInput {
  ids?: number[];
  name?: string;
  warehouseId: string;
  contactName?: string;
  phoneNumber?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  status?: number[];
  linkStatus?: number[];
  createdAt?: [string | null, string | null] | null;
  updatedAt?: [string | null, string | null] | null;
  vehicleTypeId?: number;
  standingPickup?: boolean;
  isNew?: boolean;
  continuousFailure?: boolean;
  /**
   * 地址模糊筛选。后端 `CooperationWarehouseFilters.address` 一直支持
   * （对 address_line_1+line_2 做 LIKE 并 OR 关联地址 JSON，纯文本、不比 hash），
   * 此前类型里漏声明——站点管理页靠 `...restFilters` 展开绕过了 TS 检查，AB 向导直接传则报错。
   */
  address?: string;
}

export interface ContactPersonType {
  name?: string;
  countryCode?: number;
  phone?: string;
  extension?: string;
  inBlacklist?: boolean;
}

export interface RelatedAddressType {
  address1?: string;
  address2?: string;
}

export interface CooperationWarehouseType {
  id: number;
  name?: string;
  type?: number;
  warehouseId?: number;
  status?: number;
  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  addressHash?: string;
  latitude?: number;
  longitude?: number;
  warehouseName?: string;
  contactPersonList?: ContactPersonType[];
  email?: string;
  workWeekdayList?: number[];
  workStartTime?: string;
  workEndTime?: string;
  expectedPickupStartTime?: string;
  expectedPickupEndTime?: string;
  aliasList?: string[];
  relatedAddressList?: RelatedAddressType[];
  isNew?: boolean;
  continuousFailure?: boolean;
  standingPickup?: boolean;
  createdAt?: number;
  updatedAt?: number;
  channelId?: number;
  relatedChannelIds?: number[];
  useVehicleModelId?: number;
  remark?: string;
  otherRemark?: string;
  linkStatus?: number;
  warehouseCategory?: number;
  brand?: string;
  subBrand?: string;
  dock?: string;
  groupId?: string;
  groupName?: string;
  groupQrcodeS3Key?: string;
  standingPickupChannelId?: number;
  standingScheduleJson?: string;
  /** 地址标准化状态 0未标准化 1成功 2失败；=2 时不应显示「已标准化」（其 core/analysis hash 是本地兜底） */
  standardizeStatus?: number;
  /** 命中键 core_hash：link5 站点编辑走 skipAddressStandardize 时原样回传，缺失会把站点命中键写空 */
  addressCoreHash?: string;
  /** 去小号 analysis_hash：同上 */
  addressAnalysisHash?: string;
  vehicleTypeId?: number;
}

export interface CooperationWarehouseListingResponse {
  getCooperationWarehouseListing: CooperationWarehouseListingResponseType;
}

export interface CooperationWarehouseListingResponseType {
  content: CooperationWarehouseType[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface FindCooperationWarehouseByIdResponse {
  getCooperationWarehouseById: CooperationWarehouseType;
}
